export function clampRallyIndex(index, count) {
  return Math.max(0, Math.min(Math.max(0, count - 1), index));
}

export function getRallyProgress(sectionTop, travel) {
  if (travel <= 0) return 0;
  return Math.min(1, Math.max(0, -sectionTop / travel));
}

const elementName = 'aceii-rally-flow';

if (
  typeof window !== 'undefined'
  && typeof HTMLElement !== 'undefined'
  && !window.customElements.get(elementName)
) {
  class AceiiRallyFlow extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.viewport = this.querySelector('[data-rally-viewport]');
      this.track = this.querySelector('[data-rally-track]');
      this.cards = [...this.querySelectorAll('[data-rally-card]')];
      this.previousButton = this.querySelector('[data-rally-prev]');
      this.nextButton = this.querySelector('[data-rally-next]');
      this.mobileQuery = window.matchMedia('(max-width: 760px)');
      this.currentIndex = 0;
      this.animationFrame = 0;

      if (!this.viewport || !this.track || !this.cards.length) return;

      const { signal } = this.abortController;

      this.previousButton?.addEventListener(
        'click',
        () => this.goTo(this.currentIndex - 1),
        { signal },
      );
      this.nextButton?.addEventListener(
        'click',
        () => this.goTo(this.currentIndex + 1),
        { signal },
      );
      this.viewport.addEventListener(
        'scroll',
        () => this.queueUpdate(),
        { passive: true, signal },
      );
      window.addEventListener(
        'scroll',
        () => this.queueUpdate(),
        { passive: true, signal },
      );
      window.addEventListener('resize', () => this.layout(), { signal });
      this.mobileQuery.addEventListener('change', () => this.layout(), {
        signal,
      });

      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.track);

      this.layout();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.animationFrame) {
        window.cancelAnimationFrame(this.animationFrame);
      }
      this.animationFrame = 0;
      this.abortController = null;
    }

    get isMobile() {
      return this.mobileQuery?.matches ?? false;
    }

    get maximumMovement() {
      if (!this.viewport || !this.track) return 0;
      return Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);
    }

    layout() {
      if (!this.track) return;

      if (this.isMobile) {
        this.style.height = 'auto';
        this.track.style.transform = 'none';
      } else {
        this.style.height = `${
          window.innerHeight + this.maximumMovement + 120
        }px`;
      }

      this.queueUpdate();
    }

    queueUpdate() {
      if (this.animationFrame) return;

      this.animationFrame = window.requestAnimationFrame(() => {
        this.animationFrame = 0;
        this.update();
      });
    }

    update() {
      if (!this.cards.length) return;

      if (this.isMobile) {
        const viewportLeft = this.viewport.scrollLeft;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.offsetLeft - viewportLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        this.currentIndex = closestIndex;
      } else {
        const travel = this.offsetHeight - window.innerHeight;
        const progress = getRallyProgress(
          this.getBoundingClientRect().top,
          travel,
        );

        this.track.style.transform = `translate3d(${
          -this.maximumMovement * progress
        }px, 0, 0)`;
        this.currentIndex = Math.round(progress * (this.cards.length - 1));
      }

      this.updateControls();
    }

    goTo(index) {
      const nextIndex = clampRallyIndex(index, this.cards.length);
      this.currentIndex = nextIndex;

      if (this.isMobile) {
        this.cards[nextIndex]?.scrollIntoView({
          behavior: this.reducedMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      } else {
        const sectionTop =
          window.scrollY + this.getBoundingClientRect().top;
        const travel = this.offsetHeight - window.innerHeight;
        const denominator = Math.max(1, this.cards.length - 1);

        window.scrollTo({
          top: sectionTop + travel * (nextIndex / denominator),
          behavior: this.reducedMotion ? 'auto' : 'smooth',
        });
      }

      this.updateControls();
    }

    get reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    updateControls() {
      if (this.previousButton) {
        this.previousButton.disabled = this.currentIndex <= 0;
      }
      if (this.nextButton) {
        this.nextButton.disabled =
          this.currentIndex >= this.cards.length - 1;
      }
    }
  }

  window.customElements.define(elementName, AceiiRallyFlow);
}
